// src/components/settings/FollowUpVocabularySettings.js
// SEAM 6 VERIFIER: qfup-7d3e9a41-settings
//
// Settings → Follow-up options. Edits the two lists that feed the "Log
// follow-up" pickers: how you followed up (methods) and what happened
// (results).
//
// The document (organizations/{orgId}/settings/quoteVocabularies) is
// server-write-only — firestore.rules denies client writes to it — so saves go
// through the saveQuoteVocabularies Cloud Function, which enforces the same
// rules shown here (no blanks, no duplicates, caps, at least one option).
//
// SAME DRAFT MODEL AS PipelineSettings:
//   - `base` is the saved lists + the revision the edits started from; `rows`
//     is the draft. Unsaved changes are DERIVED (draft vs base) and listed on
//     screen, so undoing an edit clears the dirty state.
//   - Save sends base.revision — the revision the EDITS STARTED FROM, never the
//     server's current one. That is what lets the server's optimistic lock fire
//     when someone else saved meanwhile (the Seam 4 bug-4 lesson).
//
// Defaults are Stacey Boychuk's vocabulary, adopted verbatim. An org that never
// opens this screen uses those; the document does not exist until a first save.
//
// qclose-3e8a5c17 — Seam 9b: ONE editor, TWO panels. The same document now
// also holds the close lists (decision factors, price feedback). Rather than a
// second copy of this draft machinery, the lists a panel edits come from its
// `group` prop: FOLLOW_UP_GROUP (the default — this panel exactly as before) or
// CLOSE_GROUP. Each panel SENDS ONLY ITS OWN LISTS; saveQuoteVocabularies
// carries every omitted list forward, so neither panel can erase the other's.
// The two share one revision, so a save in one panel while the other holds
// unsaved edits makes the other show its "changed since you started" notice —
// the safe outcome, never a silent overwrite.
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { db, functions } from '../../firebase/config';
import { doc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { Plus, Trash2, ArrowUp, ArrowDown, RotateCcw, PhoneCall, Flag } from 'lucide-react';
import { resolveQuoteVocabularies } from '../../utils/quoteSchema';
import {
    draftRowsFrom,
    newDraftRow,
    saveInputFrom,
    pendingChanges,
    hasPendingChanges,
    summarizePendingChanges,
    moveRow,
    validateDraft,
    isStaleListError,
    VOCAB_MAX_ITEMS,
    VOCAB_MAX_LENGTH,
} from '../../utils/quoteVocabularyDraft';

export const FOLLOW_UP_GROUP = {
    id: 'followUp',
    title: 'Follow-up options',
    intro: 'The choices your team picks from when logging a follow-up on an estimate.',
    noun: 'follow-up options',
    lists: [
        { key: 'followUpMethods', label: 'How you followed up', hint: 'Phone, Email, Text…', summary: 'how you followed up' },
        { key: 'followUpResults', label: 'What happened', hint: 'Spoke with client, No response…', summary: 'what happened' },
    ],
};

// qclose-3e8a5c17 — asked when an estimate is won, lost or deferred.
export const CLOSE_GROUP = {
    id: 'close',
    title: 'Close reasons',
    intro: 'The choices your team picks from when saying why an estimate was won, lost or deferred.',
    noun: 'close reasons',
    lists: [
        { key: 'closeReasons', label: 'Decision factor', hint: 'Price, Availability/timing…', summary: 'decision factor' },
        { key: 'priceFeedbackOptions', label: 'Price feedback', hint: 'How our price landed…', summary: 'price feedback' },
    ],
};

const emptyBy = (lists, v) => Object.fromEntries(lists.map(({ key }) => [key, v]));

export default function FollowUpVocabularySettings({ currentUserProfile, group = FOLLOW_UP_GROUP }) {
    const LISTS = group.lists;
    const LEAVE_WARNING = `You have unsaved ${group.noun}. Leave without saving?`;
    const orgId = currentUserProfile?.organizationId;
    const containerRef = useRef(null);

    const [serverLists, setServerLists]       = useState(null);
    const [serverRevision, setServerRevision] = useState(0);
    const [base, setBase]                     = useState(null); // { lists, revision } the draft started from
    const [rows, setRows]                     = useState(() => emptyBy(group.lists, []));
    const [adoptAtRevision, setAdoptAtRevision] = useState(null);
    const [newValue, setNewValue]             = useState(() => emptyBy(group.lists, ''));
    const [staleNotice, setStaleNotice]       = useState(false);
    const [isSaving, setIsSaving]             = useState(false);
    const [saved, setSaved]                   = useState(false);
    const [error, setError]                   = useState('');
    const [loading, setLoading]               = useState(true);

    // ── live saved lists ────────────────────────────────────────────────────
    useEffect(() => {
        if (!orgId) return undefined;
        return onSnapshot(doc(db, `organizations/${orgId}/settings`, 'quoteVocabularies'), (snap) => {
            const data = snap.exists() ? snap.data() : null;
            setServerRevision(Number.isInteger(data?.revision) ? data.revision : 0);
            setServerLists(resolveQuoteVocabularies(data));
            setLoading(false);
        }, (err) => {
            console.error('FollowUpVocabularySettings: could not load options', err);
            setError(`Could not load ${group.noun}.`);
            setLoading(false);
        });
    }, [orgId]); // eslint-disable-line react-hooks/exhaustive-deps

    const changes = useMemo(
        () => Object.fromEntries(LISTS.map(({ key }) => [key, pendingChanges(base?.lists?.[key], rows[key])])),
        [base, rows, LISTS]
    );

    const dirty = LISTS.some(({ key }) => hasPendingChanges(changes[key]));
    const changeLines = useMemo(
        () => LISTS.flatMap(({ key, summary }) => summarizePendingChanges(changes[key], summary)),
        [changes, LISTS]
    );

    // Saved elsewhere since this draft started: a save now would be refused.
    const conflict = dirty && !!base && adoptAtRevision === null && serverRevision !== base.revision;

    const adopt = useCallback((lists, revision) => {
        setBase({ lists, revision });
        setRows(Object.fromEntries(LISTS.map(({ key }) => [key, draftRowsFrom(lists[key])])));
    }, [LISTS]);

    useEffect(() => {
        if (!serverLists) return;
        if (adoptAtRevision !== null) {
            if (serverRevision >= adoptAtRevision) {
                adopt(serverLists, serverRevision);
                setAdoptAtRevision(null);
            }
            return;
        }
        if (!base || !dirty) adopt(serverLists, serverRevision);
    }, [serverLists, serverRevision, adoptAtRevision, dirty]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── leave guard ─────────────────────────────────────────────────────────
    // Mirrors the one in PipelineSettings (both panels live on the 'pipeline'
    // tab, so both must ask before unsaved edits are thrown away).
    useEffect(() => {
        if (!dirty) return undefined;
        const onBeforeUnload = (e) => { e.preventDefault(); e.returnValue = ''; };
        const onClickCapture = (e) => {
            if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
            const el = e.target && e.target.closest ? e.target.closest('a[href], [data-settings-tab]') : null;
            if (!el) return;
            if (containerRef.current && containerRef.current.contains(el)) return;
            if (el.getAttribute('data-settings-tab') === 'pipeline') return;
            if (el.tagName === 'A' && (el.getAttribute('target') === '_blank' || el.hasAttribute('download'))) return;
            if (!window.confirm(LEAVE_WARNING)) {
                e.preventDefault();
                e.stopPropagation();
            }
        };
        window.addEventListener('beforeunload', onBeforeUnload);
        document.addEventListener('click', onClickCapture, true);
        return () => {
            window.removeEventListener('beforeunload', onBeforeUnload);
            document.removeEventListener('click', onClickCapture, true);
        };
    }, [dirty, LEAVE_WARNING]);

    // ── draft edits ─────────────────────────────────────────────────────────
    const setList = (key, next) => setRows((r) => ({ ...r, [key]: next }));

    const editValue = (key, rowKey, value) =>
        setList(key, rows[key].map((r) => (r.key === rowKey ? { ...r, value } : r)));

    const toggleDelete = (key, rowKey) =>
        setList(key, rows[key]
            .map((r) => (r.key === rowKey ? { ...r, pendingDelete: !r.pendingDelete } : r))
            // a never-saved row that is removed just disappears
            .filter((r) => !(r.isNew && r.pendingDelete)));

    const move = (key, rowKey, delta) => setList(key, moveRow(rows[key], rowKey, delta));

    const addValue = (key) => {
        const v = (newValue[key] || '').trim();
        if (!v) return;
        setList(key, [...rows[key], newDraftRow(v)]);
        setNewValue((n) => ({ ...n, [key]: '' }));
    };

    const discard = () => {
        setStaleNotice(false);
        setError('');
        if (base) adopt(base.lists, base.revision);
    };

    const reloadLatest = () => {
        setStaleNotice(false);
        setError('');
        if (serverLists) adopt(serverLists, serverRevision);
    };

    // ── save ────────────────────────────────────────────────────────────────
    const handleSave = async () => {
        if (isSaving || !base) return;
        setError('');
        setStaleNotice(false);

        for (const { key, label } of LISTS) {
            const problem = validateDraft(rows[key], label);
            if (problem) { setError(problem); return; }
        }

        setIsSaving(true);
        try {
            const res = await httpsCallable(functions, 'saveQuoteVocabularies')({
                // ONLY this panel's lists — the server carries the rest forward.
                ...Object.fromEntries(LISTS.map(({ key }) => [key, saveInputFrom(rows[key])])),
                // THE revision the edits started from — never serverRevision.
                revision: base.revision,
            });
            setAdoptAtRevision(res?.data?.revision ?? base.revision + 1);
            setSaved(true);
            setTimeout(() => setSaved(false), 2500);
        } catch (err) {
            console.error('FollowUpVocabularySettings: save failed', err);
            if (isStaleListError(err)) setStaleNotice(true);
            else setError(err?.message || `Could not save ${group.noun}.`);
        } finally {
            setIsSaving(false);
        }
    };

    if (loading || !base) return <div className="p-6 text-sm text-gray-400">Loading…</div>;

    const renderList = ({ key, label, hint }) => {
        const list = rows[key] || [];
        const live = list.filter((r) => !r.pendingDelete).length;
        return (
            <div key={key} className="flex-1 min-w-[260px]">
                <h4 className="text-sm font-semibold text-gray-700">{label}</h4>
                <p className="text-xs text-gray-400 mb-2">{hint}</p>

                <ul className="space-y-2">
                    {list.map((r, i) => (
                        <li key={r.key} className="flex items-center gap-1">
                            <input
                                type="text"
                                value={r.value}
                                maxLength={VOCAB_MAX_LENGTH}
                                disabled={r.pendingDelete || isSaving}
                                onChange={(e) => editValue(key, r.key, e.target.value)}
                                className={`flex-1 px-2 py-1.5 text-sm border rounded-lg ${
                                    r.pendingDelete
                                        ? 'line-through text-gray-400 bg-gray-50 border-gray-100'
                                        : 'border-gray-200'
                                }`}
                            />
                            <button type="button" title="Move up" disabled={i === 0 || isSaving}
                                onClick={() => move(key, r.key, -1)}
                                className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30">
                                <ArrowUp size={14} />
                            </button>
                            <button type="button" title="Move down" disabled={i === list.length - 1 || isSaving}
                                onClick={() => move(key, r.key, 1)}
                                className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30">
                                <ArrowDown size={14} />
                            </button>
                            <button type="button" title={r.pendingDelete ? 'Keep' : 'Remove'} disabled={isSaving}
                                onClick={() => toggleDelete(key, r.key)}
                                className="p-1 text-gray-400 hover:text-red-600 disabled:opacity-30">
                                {r.pendingDelete ? <RotateCcw size={14} /> : <Trash2 size={14} />}
                            </button>
                        </li>
                    ))}
                </ul>

                <div className="flex items-center gap-2 mt-2">
                    <input
                        type="text"
                        value={newValue[key]}
                        maxLength={VOCAB_MAX_LENGTH}
                        placeholder="Add an option"
                        disabled={isSaving || live >= VOCAB_MAX_ITEMS}
                        onChange={(e) => setNewValue((n) => ({ ...n, [key]: e.target.value }))}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addValue(key); } }}
                        className="flex-1 px-2 py-1.5 text-sm border border-gray-200 rounded-lg disabled:opacity-60"
                    />
                    <button type="button" onClick={() => addValue(key)}
                        disabled={isSaving || live >= VOCAB_MAX_ITEMS}
                        className="flex items-center gap-1 px-2 py-1.5 text-sm text-[#5F4EC9] border border-[#5F4EC9] rounded-lg hover:bg-purple-50 disabled:opacity-40">
                        <Plus size={14} /> Add
                    </button>
                </div>
                <p className="mt-1 text-xs text-gray-400">{live} of {VOCAB_MAX_ITEMS}</p>
            </div>
        );
    };

    return (
        <div ref={containerRef} className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
            <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-800">
                {group.id === 'close'
                    ? <Flag size={18} className="text-[#5F4EC9]" />
                    : <PhoneCall size={18} className="text-[#5F4EC9]" />} {group.title}
            </h3>
            <p className="mt-1 mb-5 text-sm text-gray-500">
                {group.intro} Renaming an option here does not change answers already
                recorded — the history keeps the words that were true when it was written.
            </p>

            <div className="flex flex-wrap gap-8">{LISTS.map(renderList)}</div>

            {changeLines.length > 0 && (
                <div className="mt-5 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-sm font-medium text-amber-900">Unsaved changes</p>
                    <ul className="mt-1 text-sm text-amber-800 list-disc list-inside">
                        {changeLines.map((l, i) => <li key={i}>{l}</li>)}
                    </ul>
                </div>
            )}

            {conflict && (
                <p className="mt-4 text-sm text-amber-700">
                    These options were saved somewhere else while you were editing (by someone
                    else, or in the other options panel on this page). Saving now will be
                    refused — load the latest version first (your edits will be discarded).
                </p>
            )}

            {staleNotice && (
                <p className="mt-4 text-sm text-red-600">
                    Someone else changed these lists first, so nothing was saved. Load the
                    latest version and make your changes again.
                </p>
            )}

            {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
            {saved && <p className="mt-4 text-sm text-green-600">Saved.</p>}

            <div className="flex flex-wrap gap-2 mt-5">
                <button type="button" onClick={handleSave} disabled={!dirty || isSaving || conflict}
                    className="px-4 py-2 text-sm font-medium text-white bg-[#5F4EC9] rounded-lg hover:bg-[#4f40a8] disabled:opacity-40">
                    {isSaving ? 'Saving…' : 'Save changes'}
                </button>
                <button type="button" onClick={discard} disabled={!dirty || isSaving}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-40">
                    Discard changes
                </button>
                {(conflict || staleNotice) && (
                    <button type="button" onClick={reloadLatest} disabled={isSaving}
                        className="px-4 py-2 text-sm font-medium text-amber-800 bg-amber-100 rounded-lg hover:bg-amber-200 disabled:opacity-40">
                        Load the latest version
                    </button>
                )}
            </div>
        </div>
    );
}
